import type { CountryCode } from 'libphonenumber-js';
import React, { Activity, useMemo, type FC } from 'react';
import { type TextStyle, type ViewStyle } from 'react-native';

import { Stack } from '@/components/layout';
import { Text, TextProps } from '@/components/ui/text';
import { TextInput } from '@/components/ui/text-input/text-input';
import type {
  TextInputFieldProps,
  TextInputIconProps,
  TextInputStatus,
} from '@/components/ui/text-input/types';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

export type FormInputProps = Omit<TextInputFieldProps, 'style'> & {
  label: string;
  /** When true, only the field is shown (e.g. second address line aligned with another labeled field). */
  hideLabel?: boolean;
  /**
   * Show required asterisk (*) as in Figma
   */
  required?: boolean;
  status?: TextInputStatus;
  disabled?: boolean;
  /**
   * Icon on the right (default) or left of the input
   */
  iconProps?: TextInputIconProps;
  containerStyle?: ViewStyle | ViewStyle[];
  inputStyle?: TextStyle | TextStyle[];
  phoneNumber?: boolean;
  phoneDefaultCountry?: CountryCode;
  /**
   * Input value
   */
  value?: string | null;
  /**
   * Input onChangeText handler
   */
  onChangeText?: (text: string) => void;
  /**
   * Input onSubmitEditing handler
   */
  onSubmitEditing?: () => void;
  /**
   * Input onFocus handler
   */
  onFocus?: () => void;
  /**
   * Trim input value
   */
  trim?: boolean;
  labelProps?: Omit<TextProps, 'children' | 'style'>;
  errorMessage?: string;
  onCountryCodeChange?: (countryCode: CountryCode | undefined) => void;
};

export const FormInput: FC<FormInputProps> = ({
  label,
  hideLabel = false,
  required,
  status = 'default',
  disabled = false,
  iconProps,
  containerStyle,
  inputStyle,
  phoneNumber = false,
  phoneDefaultCountry,
  placeholder,
  trim = false,
  labelProps,
  onBlur,
  onFocus,
  errorMessage,
  ...fieldProps
}) => {
  const styles = useStyles();
  const { scale } = useScaling();
  const displayLabel = label?.toUpperCase?.() ?? '';
  const resolvedPlaceholder = placeholder ?? label;

  const iconPosition = iconProps?.position ?? 'right';

  const computedInputStyle = useMemo<TextStyle[]>(() => {
    const stylesArray: TextStyle[] = [{ paddingHorizontal: scale(0), marginHorizontal: scale(0) }];

    if (iconPosition === 'left' || phoneNumber) {
      stylesArray.push(styles.noIconRightTextInputStyle);
    } else if (iconPosition === 'right') {
      stylesArray.push(styles.noIconLeftTextInputStyle);
    }

    if (Array.isArray(inputStyle)) {
      stylesArray.push(...inputStyle);
    } else if (inputStyle) {
      stylesArray.push(inputStyle);
    }

    return stylesArray;
  }, [
    iconPosition,
    inputStyle,
    phoneNumber,
    scale,
    styles.noIconLeftTextInputStyle,
    styles.noIconRightTextInputStyle,
  ]);

  return (
    <Stack direction="column" gap={hideLabel ? 0 : 8} width="100%" style={containerStyle}>
      {!hideLabel && (
        <Stack direction="row" align="center" justify="flex-start">
          <TextInput.Label
            variant="Font Inter/Subtitle/Regular 11px"
            style={styles.label}
            {...labelProps}>
            {displayLabel}
          </TextInput.Label>
          {required && (
            <Text variant="Font Inter/Subtitle/Regular 11px" style={styles.requiredMark}>
              *
            </Text>
          )}
        </Stack>
      )}

      <TextInput
        status={disabled ? 'disabled' : status}
        disabled={disabled}
        value={fieldProps.value ?? undefined}
        onChangeText={fieldProps.onChangeText}
        onCountryCodeChange={fieldProps.onCountryCodeChange}
        onSubmitEditing={() => {
          if (trim) {
            fieldProps.onChangeText?.(fieldProps.value?.trim() ?? '');
          }
          fieldProps.onSubmitEditing?.();
        }}
        onBlur={e => {
          if (trim) {
            fieldProps.onChangeText?.(fieldProps.value?.trim() ?? '');
          }
          onBlur?.(e);
        }}
        onFocus={onFocus}
        phoneNumber={phoneNumber}
        phoneDefaultCountry={phoneDefaultCountry}>
        {iconProps && iconPosition === 'left' && <TextInput.Icon {...iconProps} position="left" />}
        <TextInput.Content paddingX={0} marginX={0} style={{ paddingHorizontal: scale(6) }}>
          {phoneNumber && (
            <TextInput.Flag styleWrapper={{ marginLeft: scale(8) }} position="left" />
          )}
          <TextInput.Field
            {...fieldProps}
            variant="Font Inter/Subtitle/Regular 14px"
            placeholder={resolvedPlaceholder}
            style={computedInputStyle}
          />
          {iconProps && iconPosition === 'right' && (
            <TextInput.Icon {...iconProps} position="right" />
          )}
        </TextInput.Content>
        <Activity mode={errorMessage ? 'visible' : 'hidden'}>
          <TextInput.HelperText>{errorMessage}</TextInput.HelperText>
        </Activity>
      </TextInput>
    </Stack>
  );
};

const useStyles = createStyles(({ scale }) => ({
  noIconTextInputStyle: {
    marginHorizontal: scale(16),
  },
  noIconLeftTextInputStyle: {
    marginLeft: scale(8),
  },
  noIconRightTextInputStyle: {
    marginRight: scale(16),
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: scale(8),
  },
  label: {
    color: colors['Labels/Primary'],
  },
  requiredMark: {
    marginLeft: scale(4),
    color: colors.Red,
  },
}));
